/**
 * Hub loading skeleton — same regions as HubInteractiveScreen (back, title, card, footer).
 *
 * Card always swipes in here. Loaded interactive card must not swipe again that visit.
 *
 * Belongs here: loading placeholder UI only.
 */
import BackButton from '../../../../components/ui-components/BackButton'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { getBottomPadding } from '../../../../utils/sessionStorage'
import { EventNames } from '../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../utils/appBridge'
import { ExpertReviewEventIds, ExpertReviewScreen } from '../analytics/expertReviewEvents'
import { expertReviewSafeAreaTopStyle } from '../shared/expertReviewSafeArea'
import {
  DirectionalSlideSwap,
  SlideDirection
} from '../shared/transitions/DirectionalSlideSwap'
import { CardBg } from './cardBg'
import '../../../../components/skeletons/skeleton.css'

export function HubPageSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <div
      style={{
        ...expertReviewSafeAreaTopStyle(),
        paddingBottom: `calc(${getBottomPadding()}px + 16px)`
      }}
      className="relative flex h-screen flex-col overflow-hidden bg-white px-5"
    >
      {/* Back stays fixed. Title scrolls with the card (matches HubInteractiveScreen). */}
      <header className="relative z-10 shrink-0">
        <BackButton
          testLocator="allocation-plan-loading-back"
          onClick={() => {
            lightImpactVibrate()
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                ScreenName: ExpertReviewScreen.Hub,
                eventId: ExpertReviewEventIds.Back,
                buttonLabel: 'Back'
              }
            })
            onBack()
          }}
        />
      </header>

      <div className="relative z-0 flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto no-scrollbar">
          <div className="pb-6">
            <div className="skeleton mt-4 h-[34px] w-[220px] rounded-[4px]" />
            <div className="skeleton mt-2 h-[13px] w-full max-w-[323px] rounded-[4px]" />
            <div className="skeleton mt-2 h-[13px] w-[72%] max-w-[323px] rounded-[4px]" />
          </div>
          <DirectionalSlideSwap
            contentKey="expert-review-hub-skeleton"
            direction={SlideDirection.Forward}
          >
            <div className="relative w-full overflow-hidden rounded-[16px] bg-[#F0F4F7]">
              <div
                className="pointer-events-none absolute right-0 top-0 h-[236px] w-[256px] [&>div]:h-full [&>div]:w-full [&>div>svg]:h-full [&>div>svg]:w-full"
                style={{
                  maskImage: 'linear-gradient(to right, transparent 0%, black 45%)',
                  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 45%)',
                  opacity: 0.75
                }}
                aria-hidden
              >
                <RenderHTML innerHTMLText={CardBg} className="h-full w-full" />
              </div>
              <div className="relative px-6 pt-6 pb-5 min-h-[188px]">
                <div className="skeleton h-3 w-24 rounded-[4px]" />
                <div className="skeleton mt-8 h-[13px] w-40 rounded-[4px]" />
                <div className="skeleton mt-1 h-[22px] w-36 rounded-[4px]" />
                <div className="skeleton mt-[2px] h-[22px] w-24 rounded-[4px]" />
              </div>
              <div className="relative mx-[2px] mb-[2px] overflow-hidden rounded-[14px] bg-white">
                <HubStepRowSkeleton />
                <div className="h-px bg-[#F0F4F7]" />
                <HubStepRowSkeleton />
                <div className="h-px bg-[#F0F4F7]" />
                <HubStepRowSkeleton />
              </div>
            </div>
          </DirectionalSlideSwap>
          <div className="h-[110px] shrink-0" aria-hidden />
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-20">
        <div
          className="bg-white px-5"
          style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
        >
          <div className="flex flex-col items-center gap-2 rounded-[16px] border border-[#F0F4F7] bg-white pt-2">
            <div className="skeleton h-[13px] w-48 rounded-[4px]" />
            <div className="skeleton h-[56px] w-full rounded-[16px]" />
          </div>
        </div>
      </div>
    </div>
  )
}

export function HubStepRowSkeleton() {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-5">
      <div className="flex min-w-0 items-start gap-4">
        <div className="skeleton mt-0.5 h-10 w-10 shrink-0 rounded-[4px]" />
        <div className="min-w-0 flex-1">
          <div className="skeleton h-[18px] w-28 rounded-[4px]" />
          <div className="skeleton mt-[3px] h-[14px] w-20 rounded-[4px]" />
        </div>
      </div>
      <div className="skeleton h-4 w-2.5 shrink-0 rounded-[2px]" />
    </div>
  )
}
